import { isPlatformBrowser } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Inject, OnDestroy, OnInit, PLATFORM_ID, ViewChild } from '@angular/core';
import type * as Leaflet from 'leaflet';
import { LanguageService } from 'app/services/language/language.service';
import { ActivatedRoute, Router } from '@angular/router';
import { EventsService } from '../../../services/events/events.service';
import { ConfigService } from 'app/services/config/config.service';
import { LocationCongigGlobal } from 'app/models/config-global-model';
import { AppService } from 'app/services/app/app.service';
import { RestService } from 'app/services/rest/rest.service';
import { Utils } from 'app/components/utils/Utils';
import { ParameterCart } from 'app/models/cart-model';
import { loadLeaflet } from 'app/components/utils/load-leaflet';

interface LockerLocationViewModel extends LocationCongigGlobal {
  slug: string;
  addressLine: string;
  cityLine: string;
  latitude?: number;
  longitude?: number;
}



@Component({
    selector: 'app-locker-location',
    templateUrl: './locker-location.page.html',
    styleUrls: ['./locker-location.page.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LockerLocationPage implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('mapContainer') private mapContainer?: ElementRef<HTMLDivElement>;

  locations: LockerLocationViewModel[] = [];
  selectedLocation: LockerLocationViewModel | null = null;
  isReserving = false;
  private leaflet?: typeof Leaflet;
  private map?: Leaflet.Map;
  private markers = new Map<LockerLocationViewModel, Leaflet.Marker>();
  private resizeObserver?: ResizeObserver;
  private destroyed = false;

  constructor(
    private configService: ConfigService,
    private eventsService: EventsService,
    private appService: AppService,
    private restService: RestService,
    private router: Router,
    private language: LanguageService,
    private route: ActivatedRoute,
    @Inject(PLATFORM_ID) private platformId: object,
    private changeDetector: ChangeDetectorRef
  ) {}

  ngOnInit() {
    const rawLocations = this.configService.settings?.locations || [];
    this.locations = rawLocations.map((location) => this.mapLocation(location));
    if (this.locations.length === 1) {
      this.reserveLocation(this.locations[0]);
      return;
    }

    this.selectLocation(this.locations[0] || null);
  }

  ngAfterViewInit() {
    this.eventsService.publish('refreshStep', { title: "0/4", nameBt: null });
    void this.initializeMap();
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.resizeObserver?.disconnect();
    this.map?.remove();
  }

  selectLocation(location: LockerLocationViewModel | null) {
    this.selectedLocation = location;
    this.updateMarkerIcons();
    if (location && this.hasCoordinates(location) && this.map) {
      this.map.setView([location.latitude as number, location.longitude as number], 16, { animate: true });
    }
  }

  reserveLocation(location: LockerLocationViewModel) {
    if (!location || this.isReserving) {
      return;
    }

    const kiosk = location.code || location.name;
    this.isReserving = true;
    this.appService.kiosk = kiosk;
    this.appService.locationId = location.id;

    this.restService.getStockLockers().subscribe({
      next: (stock) => {
        if (stock != undefined) {
          this.appService.setCurrentStock(stock);
        }
        const parameters:ParameterCart = Utils.getParams(this.route.snapshot.queryParamMap);
        this.router.navigate([this.language.path('reserve'), kiosk, 'locker-type'], { queryParams: parameters });
      },
      error: () => {
        this.isReserving = false;
      },
      complete: () => {
        this.isReserving = false;
      }
    });
  }

  trackByLocation(_index: number, location: LockerLocationViewModel) {
    return location.id || location.code || location.name;
  }

  private mapLocation(location: LocationCongigGlobal): LockerLocationViewModel {
    const slug = this.slugify(location.code || location.name);

    return {
      ...location,
      slug,
      addressLine: location.address || 'Ubicacion disponible en reserva online',
      cityLine: location.city || '',
      latitude: this.toCoordinate(location.latitude),
      longitude: this.toCoordinate(location.longitude)
    };
  }

  private toCoordinate(value: unknown): number | undefined {
    if (value == null || value === '') {
      return undefined;
    }
    const coordinate = Number(value);
    return Number.isFinite(coordinate) ? coordinate : undefined;
  }

  private async initializeMap(): Promise<void> {
    if (!isPlatformBrowser(this.platformId) || !this.mapContainer) {
      return;
    }

    const leaflet = await loadLeaflet();
    if (this.destroyed || !this.mapContainer) {
      return;
    }

    this.leaflet = leaflet;
    this.map = leaflet.map(this.mapContainer.nativeElement, {
      scrollWheelZoom: false,
    }).setView([36.72, -4.425], 13);

    leaflet.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19,
    }).addTo(this.map);

    for (const location of this.locations) {
      if (!this.hasCoordinates(location)) {
        continue;
      }

      const marker = leaflet.marker([location.latitude as number, location.longitude as number], {
        icon: this.pinIcon(location === this.selectedLocation),
        title: location.name,
        alt: location.name,
      }).addTo(this.map);
      marker.on('click', () => {
        this.selectLocation(location);
        this.changeDetector.detectChanges();
      });
      this.markers.set(location, marker);
    }

    if (this.markers.size) {
      this.map.fitBounds(leaflet.latLngBounds(
        Array.from(this.markers.values(), (marker) => marker.getLatLng()),
      ), { padding: [36, 36], maxZoom: 15 });
    }

    this.resizeObserver = new ResizeObserver(() => this.map?.invalidateSize());
    this.resizeObserver.observe(this.mapContainer.nativeElement);
  }

  private updateMarkerIcons(): void {
    this.markers.forEach((marker, location) => {
      marker.setIcon(this.pinIcon(location === this.selectedLocation));
    });
  }

  private pinIcon(active: boolean): Leaflet.DivIcon {
    const color = active ? '#c90067' : '#f01483';
    return this.leaflet!.divIcon({
      className: 'locker-map-point',
      html: `<svg width="48" height="48" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="${color}" stroke="white" stroke-width="0.6"/></svg>`,
      iconSize: [48, 48],
      iconAnchor: [24, 48],
    });
  }

  hasCoordinates(location: LockerLocationViewModel | null): boolean {
    return Boolean(location && Number.isFinite(location.latitude) && Number.isFinite(location.longitude));
  }

  private slugify(value: string): string {
    return (value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  }

}
